import { useQueryClient } from '@tanstack/react-query';
import { Formik, Form } from 'formik';
import { isEmpty, castArray } from 'lodash';
import styled from 'styled-components';

import { Avatar } from '@hivebrite/design-system/Avatar';
import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { toast } from '@hivebrite/design-system/Toast';

import { useJobContext } from 'hb-react/frontoffice/apps/JobsV2/contexts/JobContext';
import { ITEMS_QUERY_KEYS } from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/utils/queryUtils';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import {
  Hidden,
  Textarea,
  File,
} from 'hb-react/shared/components/formik/components/inputs';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import { completeName } from 'helpers/name';

const ALLOWED_FILE_FORMATS = ['.pdf'];

const JobApplyForm = ({ jobId, jobUser, onClose }) => {
  const { currentUser, currentNetwork } = useDefaultAppContexts();

  const { setHasCurrentUserAlreadyApplied } = useJobContext();
  const { topicId } = useTopicContext();
  const queryClient = useQueryClient();

  const handleSubmit = (
    {
      applicant: {
        request_id: requestId,
        message,
        attachment,
        attachment_2: attachment2,
      },
    },
    { setSubmitting },
  ) => {
    const path = Routes.user_business_opportunities_request_applicants_path({
      user_id: currentUser.id,
      request_id: jobId,
    });

    const formData = new FormData();

    formData.append('applicant[request_id]', requestId);
    formData.append('applicant[message]', message);
    if (attachment) {
      formData.append('applicant[attachment]', attachment);
    }
    if (attachment2) {
      formData.append('applicant[attachment_2]', attachment2);
    }

    axios
      .post(path, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((_response) => {
        toast.success(I18n.t('web.business_opportunities.apply_success_email'));
        setHasCurrentUserAlreadyApplied(true);
        queryClient.invalidateQueries({
          queryKey: [ITEMS_QUERY_KEYS.jobs.items, topicId],
        });
        onClose();
      })
      .catch((error) => {
        const errors = error?.response?.data?.errors;
        let errorMessage = isEmpty(errors)
          ? __('Sorry, an error occurred')
          : // eslint-disable-next-line @typescript-eslint/no-use-before-define
            castArray(errorMessage).join(' - ');

        if (errors?.user_id[0] === 'has already applied') {
          errorMessage = s__('JobsV2|You have already applied for this job.');
        }

        toast.error(errorMessage);

        setSubmitting(false);
      });
  };

  return (
    <Formik
      initialValues={{
        applicant: {
          message: I18n.t(
            'web.business_opportunities.applicants.new.i_apply_2',
            {
              recipient_name: jobUser && completeName(jobUser),
              sender_name: completeName(currentUser),
              network_name: currentNetwork.title,
            },
          ),
          request_id: jobId,
        },
      }}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <StyledForm>
          <Hidden name="applicant[request_id]" />
          <Textarea
            name="applicant[message]"
            label={I18n.t(
              'web.business_opportunities.applicants.new.why_interested',
            )}
            placeholder={__('Type in description here')}
            minHeight={160}
          />
          <Box>
            {I18n.t(
              'web.business_opportunities.applicants.new.add_an_attachment',
            )}
          </Box>
          <Box>
            {s__('EmailCampaign|Please upload a file smaller than 10 MB.')}
          </Box>
          <File name="applicant[attachment]" formats={ALLOWED_FILE_FORMATS} />
          <File name="applicant[attachment_2]" formats={ALLOWED_FILE_FORMATS} />
          <Box>
            {I18n.t('web.business_opportunities.applicants.new.contact_title')}
          </Box>
          <Box mt={6} alignItems="center">
            <Avatar
              src={currentUser.photo_thumb_url}
              size={60}
              alt={`Profile picture of ${currentUser.name}`}
            />
            <Box flexDirection="column" px={4}>
              {currentUser && (
                <Box font="h4-semibold">
                  <Dotdotdot clamp={2}>{completeName(currentUser)}</Dotdotdot>
                </Box>
              )}
              <Box font="metadata-regular-asphalt">{currentUser.email}</Box>
            </Box>
          </Box>
          <Box justifyContent="flex-end">
            <Button
              mr={5}
              onClick={onClose}
              isDisabled={isSubmitting}
              variant="secondary"
            >
              {__('Cancel')}
            </Button>
            <Button
              as="button"
              variant="primary"
              type="submit"
              isLoading={isSubmitting}
            >
              {__('Send')}
            </Button>
          </Box>
        </StyledForm>
      )}
    </Formik>
  );
};

export default JobApplyForm;

const StyledForm = styled(Form)`
  flex: 1 1 100%;
`;
